// ============================================================================
// LA GARDE ANTI-DOUBLON DES RÉFÉRENTIELS — « Ressemble à « Vestes » — 86 % ».
//
// UTILITÉ : quand on crée une valeur de référentiel (famille, couleur, secteur…), la vraie menace n'est
// pas la faute de frappe, c'est le SYNONYME. « Vestes », « veste », « Veste homme » : trois lignes pour
// une seule notion, et plus personne ne sait laquelle filtre quoi. Une fois le référentiel pollué, on
// ne le nettoie plus — les articles pointent dessus.
//
// ⚠️ DEUX NIVEAUX, ET ILS N'ONT PAS LE MÊME POUVOIR :
//   · EXACT (`keyOf` : accents, casse et ORDRE DES MOTS ignorés) → on n'offre PAS de créer, on propose
//     de CHOISIR l'existante. « Veste homme » et « homme veste » sont la même chose ;
//   · RESSEMBLANT (Dice ≥ 0,72) → on AVERTIT, et on laisse les deux issues ouvertes. Bloquer serait
//     faux : « Robe » et « Robes » se ressemblent à 89 % et peuvent être deux familles distinctes.
//     C'est un doute qu'on porte à l'écran, pas une décision qu'on prend à la place de l'opérateur.
//
// ⚠️ LE SEUIL ET L'ALGORITHME SONT CEUX DE LA MAQUETTE (0,72, Dice sur bigrammes) : ils ne sont pas
// « à peu près » — un seuil plus bas noierait l'avertissement sous les faux positifs et on cesserait de
// le lire, un seuil plus haut laisserait passer précisément les paires qui posent problème.
//
// ⚠️ LA VALEUR COMPARÉE N'EST JAMAIS LA VALEUR STOCKÉE : on plie accents et casse pour COMPARER, on
// écrit ce que l'opérateur a tapé. Normaliser à l'écriture lui retirerait ses majuscules.
// ============================================================================

/** Espaces réduits, bords coupés — la valeur telle qu'on l'écrira. */
export function normalizeLabel(s: string): string {
  return String(s ?? '').replace(/\s+/g, ' ').trim();
}

/** Sans accents ni casse — la valeur telle qu'on la COMPARE. Jamais stockée. */
function plier(s: string): string {
  return normalizeLabel(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * La CLÉ d'identité d'un libellé : plié, puis mots TRIÉS.
 * ⚠️ Le tri des mots est ce qui rend « Veste homme » et « homme veste » identiques — deux saisies du
 * même rayon, à des mois d'écart, par deux personnes différentes.
 */
export function labelKey(s: string): string {
  return plier(s).split(' ').filter(Boolean).sort().join(' ');
}

/**
 * Coefficient de Dice sur bigrammes — 0 (rien de commun) à 1 (identique).
 * ⚠️ Choisi plutôt qu'une distance d'édition parce qu'il est INSENSIBLE À LA LONGUEUR : « Robe » vs
 * « Robes » et « Chemisier » vs « Chemisiers » sortent au même niveau, alors qu'une distance brute
 * jugerait le premier bien plus proche.
 */
export function dice(a: string, b: string): number {
  const x = plier(a).replace(/[^a-z0-9]/g, '');
  const y = plier(b).replace(/[^a-z0-9]/g, '');
  if (x === y) return 1;
  if (x.length < 2 || y.length < 2) return 0;
  const sacs = new Map<string, number>();
  for (let i = 0; i < x.length - 1; i += 1) {
    const g = x.slice(i, i + 2);
    sacs.set(g, (sacs.get(g) ?? 0) + 1);
  }
  let communs = 0;
  for (let i = 0; i < y.length - 1; i += 1) {
    const g = y.slice(i, i + 2);
    const n = sacs.get(g) ?? 0;
    if (n > 0) { sacs.set(g, n - 1); communs += 1; }
  }
  return (2 * communs) / (x.length - 1 + y.length - 1);
}

/** Au-delà de ce score, on AVERTIT. Relevé de la maquette — voir l'en-tête. */
export const SEUIL_RESSEMBLANCE = 0.72;

export type LabelVerdict =
  | { etat: 'vide' }
  /** Une valeur EXISTE déjà sous cette identité : on la choisit, on n'en crée pas une seconde. */
  | { etat: 'exact'; valeur: string; candidat: string }
  /** Une valeur PROCHE existe : on avertit, les deux issues restent ouvertes. */
  | { etat: 'proche'; valeur: string; candidat: string; pourcent: number }
  /** Rien d'approchant : création franche. */
  | { etat: 'neuf'; valeur: string };

/**
 * Ce libellé est-il neuf, connu, ou suspect ?
 * ⚠️ La liste comparée doit être celle des libellés RÉELLEMENT offerts : comparer à une liste
 * plafonnée ferait déclarer « neuf » ce qui existe au-delà du plafond, et c'est exactement ainsi qu'on
 * crée un doublon en croyant bien faire.
 */
export function checkLabel(brut: string, existants: readonly string[]): LabelVerdict {
  const valeur = normalizeLabel(brut);
  if (!valeur) return { etat: 'vide' };

  const cle = labelKey(valeur);
  const exact = existants.find((x) => labelKey(x) === cle);
  if (exact) return { etat: 'exact', valeur, candidat: exact };

  let meilleur: string | null = null;
  let score = 0;
  for (const x of existants) {
    const d = dice(valeur, x);
    if (d > score) { score = d; meilleur = x; }
  }
  if (meilleur && score >= SEUIL_RESSEMBLANCE) {
    return { etat: 'proche', valeur, candidat: meilleur, pourcent: Math.round(score * 100) };
  }
  return { etat: 'neuf', valeur };
}
